/**
 * @wl/widget-mortgage — Lead-form модалка.
 *
 * Управляет:
 *  - Открытие/закрытие модалки (Escape, click outside, X)
 *  - Маска телефона +7 (XXX) XXX-XX-XX
 *  - Валидация имени, телефона, согласия
 *  - Submit → POST submitEndpoint (mock в Этапе 2)
 *  - Success state (показ 3 сек, потом auto-close)
 *  - Focus trap (минимальный — фокус на первое поле при открытии)
 */

import { formatPhone } from '@wl/shared-ui/phone';
import type { LeadPayload } from './types';

/** Политика обработки ПДн Оператора — открывается по ссылке в тексте согласия. */
const POLICY_URL = 'https://widgy.site/politika';

/**
 * SHA-256 строки → hex (64 символа), формат колонки leads.consent_text_hash.
 *
 * crypto.subtle доступен только в защищённом контексте (https или localhost).
 * Виджет ставится и на http-страницы — там возвращаем null и просто не шлём
 * поле: оно необязательное, лид дороже хеша. Тихо ронять отправку нельзя —
 * это повторило бы историю с лидами, которые 11 дней не доходили до CRM.
 */
async function sha256Hex(text: string): Promise<string | null> {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle || !text) return null;
    const buf = await subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

/**
 * Потолок ожидания запроса на отправку лида. Без него подвисший (не упавший)
 * API держал бы кнопку «Получить предложения» в «Отправляем…» вечно — тот же
 * приём и тот же случай, что уже чинили в чате и витрине (commit 90285f3,
 * аудит 14.09.2026). Старый браузер без AbortSignal.timeout ведёт себя как раньше.
 */
function within(ms: number): { signal?: AbortSignal } {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? { signal: AbortSignal.timeout(ms) }
    : {};
}

const BANK_NAMES_FALLBACK: Record<string, string> = {
  metallinvest: 'Металлинвестбанк',
  metall: 'Металлинвестбанк',
  alfa: 'Альфа-Банк',
  vtb: 'ВТБ',
  sber: 'Сбербанк',
};

export interface LeadFormDeps {
  /** Shadow root (поиск элементов делается через него). */
  root: ShadowRoot;
  /** Submit URL. */
  submitEndpoint: string;
  /** Tenant slug — попадает в payload. */
  tenantId: string;
  /**
   * Момент монтажа виджета на странице (Date.now() из connectedCallback), а
   * НЕ момент открытия формы — так покупатель, который открыл форму с уже
   * подставленными автозаполнением полями и нажал «Отправить» за 2 секунды,
   * не попадает под серверную ловушку ботов (leads.ts: submittedAt-mountedAt<3000мс).
   */
  mountedAt: number;
  /**
   * ID виджета (объекта) — ОБЯЗАН уходить в payload, иначе сервер приписывает лид
   * произвольному виджету тенанта (Заречье→Чайковский). Берётся из cfg.widgetId.
   */
  widgetId?: string;
  /**
   * Возвращает СВЕЖИЙ embed-токен (HMAC, TTL 15 мин) для заголовка X-Embed-Token.
   * Без валидного токена сервер отбивает лид 403 (leads.ts). null → сеть недоступна:
   * submit покажет ошибку, а не сфабрикует успех.
   */
  getEmbedToken: () => Promise<string | null>;
  /**
   * Текущие значения слайдеров и выбранная программа — getter. `bankId` — банк, по
   * которому открыта форма: платёж в заявке считается для него, а не для лучшего.
   */
  getSnapshot: (bankId?: string) => Pick<
    LeadPayload,
    | 'programId'
    | 'propertyCost'
    | 'downPayment'
    | 'termYears'
    | 'monthlyPayment'
    | 'familyChildren'
    | 'familyDisabledChild'
  > & { bankId?: string };
  /** Имя банка для подписи в модалке (по bank.id). */
  bankNames: Record<string, string>;
  /** Callback после успешного submit (для emit события 'wl:lead-submitted'). */
  onSuccess?: (lead: LeadPayload) => void;
}

export class LeadForm {
  private overlay: HTMLElement;
  private formEl: HTMLElement;
  private successEl: HTMLElement;
  private bankInfoEl: HTMLElement;
  private errorEl: HTMLElement;
  private inpName: HTMLInputElement;
  private inpPhone: HTMLInputElement;
  private inpConsent: HTMLInputElement;
  private currentBankId: string | undefined;

  private autoCloseTimer: ReturnType<typeof setTimeout> | undefined;
  private keydownHandler: (e: KeyboardEvent) => void;

  constructor(private deps: LeadFormDeps) {
    const { root } = deps;
    this.overlay = mustQuery<HTMLElement>(root, '[data-modal="overlay"]');
    this.formEl = mustQuery<HTMLElement>(root, '[data-modal="form"]');
    this.successEl = mustQuery<HTMLElement>(root, '[data-modal="success"]');
    this.bankInfoEl = mustQuery<HTMLElement>(root, '[data-modal="bank-info"]');
    this.errorEl = mustQuery<HTMLElement>(root, '[data-modal-error]');
    this.inpName = mustQuery<HTMLInputElement>(root, '[data-input="name"]');
    this.inpPhone = mustQuery<HTMLInputElement>(root, '[data-input="phone"]');
    this.inpConsent = mustQuery<HTMLInputElement>(root, '[data-input="consent"]');

    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.overlay.classList.contains('open')) {
        this.close();
      }
    };

    this.bindEvents();
  }

  private bindEvents(): void {
    const { root } = this.deps;

    // CTA — открыть без выбора банка
    root.querySelector('[data-action="open-modal"]')?.addEventListener('click', () => {
      this.open();
    });

    // Форму открывают ровно два узла — делегированный listener на root, потому что
    // строки списка пересобираются в recalc через innerHTML:
    //   [data-hero]      — карточка лучшего предложения (только при role="button":
    //                      при leadForm===false template его не проставляет);
    //   [data-offer-cta] — кнопка заявки ВНУТРИ раскрытой плашки банка.
    //
    // Сама строка банка (.wl-offer / [data-offer-head]) сюда НЕ входит намеренно:
    // она разворачивает условия на месте (bindOfferExpand в index.ts). Пока строка
    // висела на этом обработчике, покупатель, нажавший «Сбербанк», получал анкету
    // захвата контактов и не мог посмотреть ни ставку, ни ПСК нужного ему банка.
    const LEAD_TRIGGERS = '[data-hero],[data-offer-cta]';
    const opensLead = (el: HTMLElement | null | undefined): boolean =>
      !!el && (el.hasAttribute('data-offer-cta') || el.getAttribute('role') === 'button');

    root.addEventListener('click', (e) => {
      const el = (e.target as Element | null)?.closest<HTMLElement>(LEAD_TRIGGERS);
      if (opensLead(el)) this.open(el!.dataset['bank']);
    });
    root.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if (ke.key !== 'Enter' && ke.key !== ' ') return;
      const el = (ke.target as Element | null)?.closest<HTMLElement>(LEAD_TRIGGERS);
      if (opensLead(el)) {
        ke.preventDefault();
        this.open(el!.dataset['bank']);
      }
    });

    // Закрытие
    root.querySelector('[data-action="close-modal"]')?.addEventListener('click', () => {
      this.close();
    });
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });
    document.addEventListener('keydown', this.keydownHandler);

    // Маска телефона
    this.inpPhone.addEventListener('input', () => {
      this.inpPhone.value = formatPhone(this.inpPhone.value);
      this.inpPhone.removeAttribute('aria-invalid');
    });
    this.inpPhone.addEventListener('focus', () => {
      if (!this.inpPhone.value) this.inpPhone.value = '+7 ';
    });

    // Снимаем красную обводку конкретного поля, как только за него взялись —
    // строка ошибки наверху остаётся до следующей попытки отправки.
    this.inpName.addEventListener('input', () => this.inpName.removeAttribute('aria-invalid'));
    this.inpConsent.addEventListener('change', () => this.inpConsent.removeAttribute('aria-invalid'));

    // Policy link
    root.querySelector('[data-action="open-policy"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      // Политика обработки ПДн Оператора. До 04.08.2026 здесь стоял адрес
      // wlabel.pro/legal/personal-data, которого НЕ СУЩЕСТВУЕТ — 404. Посетитель
      // сайта клиента ставил галку согласия и не мог открыть документ, под
      // которым подписывается. Проверено на боевом виджете Игротэка.
      // TODO: дать клиенту подставить СВОЮ политику (он оператор этих данных) —
      // это требует поля policyUrl в widget-config, отдельной волной, целиком.
      window.open(POLICY_URL, '_blank', 'noopener');
    });

    // Submit
    root.querySelector('[data-action="submit-lead"]')?.addEventListener('click', () => {
      void this.submit();
    });
  }

  open(bankId?: string): void {
    this.currentBankId = bankId;
    this.hideError();
    this.clearInvalid();
    const bankName =
      (bankId && (this.deps.bankNames[bankId] ?? BANK_NAMES_FALLBACK[bankId])) ?? 'банков';
    this.bankInfoEl.textContent = `Отправим предложения ${bankName} и других банков по вашим параметрам.`;
    this.overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    setTimeout(() => this.inpName.focus(), 240);
  }

  close(): void {
    this.overlay.classList.remove('open');
    document.body.style.overflow = '';
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = undefined;
    }
    // Сброс через 300 мс (после fade-out)
    setTimeout(() => {
      this.formEl.classList.remove('hidden');
      this.successEl.classList.remove('show');
      this.inpName.value = '';
      this.inpPhone.value = '';
      this.inpConsent.checked = false;
    }, 300);
  }

  /** Текст согласия так, как он отрисован рядом с галкой. */
  private readConsentText(): string {
    const el = this.deps.root.querySelector<HTMLElement>('[data-consent-text]');
    return (el?.textContent ?? '').replace(/\s+/g, ' ').trim();
  }

  private async submit(): Promise<void> {
    const name = this.inpName.value.trim();
    const phone = this.inpPhone.value.trim();
    const phoneDigits = phone.replace(/\D/g, '');
    const consent = this.inpConsent.checked;

    const invalid: HTMLElement[] = [];
    if (!name) invalid.push(this.inpName);
    if (phoneDigits.length < 11) invalid.push(this.inpPhone);
    if (!consent) invalid.push(this.inpConsent);

    if (invalid.length > 0) {
      this.markInvalid(invalid);
      this.showError('Заполните имя, телефон (11 цифр) и дайте согласие на обработку данных.');
      invalid[0]!.focus();
      return;
    }
    this.clearInvalid();
    this.hideError();

    const snapshot = this.deps.getSnapshot(this.currentBankId);
    const payload: LeadPayload = {
      tenantId: this.deps.tenantId,
      ...(this.deps.widgetId ? { widgetId: this.deps.widgetId } : {}),
      name,
      phone: '+' + phoneDigits, // E.164 RU: +7XXXXXXXXXX
      programId: snapshot.programId,
      propertyCost: snapshot.propertyCost,
      downPayment: snapshot.downPayment,
      termYears: snapshot.termYears,
      consent: true,
      pageUrl: window.location.href,
      // Ловушка ботов на сервере (leads.ts): submittedAt-mountedAt<3000мс → лид
      // молча отбрасывается. mountedAt — момент монтажа ВИДЖЕТА (не формы),
      // поэтому живой покупатель, даже с автозаполнением и мгновенным submit
      // формы, к этому моменту уже держит виджет на экране дольше порога.
      mountedAt: this.deps.mountedAt,
      submittedAt: Date.now(),
    };

    // Контрольная сумма текста согласия, который человек видел на экране.
    // Без неё в базе оставалась только отметка «согласие получено», а чем
    // доказать ФОРМУЛИРОВКУ — нечем; именно её спрашивают при проверке.
    // Берём текст из отрисованного DOM, а не из константы: если вёрстка
    // разъедется с кодом, хеш зафиксирует то, что реально было перед глазами.
    const consentHash = await sha256Hex(this.readConsentText());
    if (consentHash) payload.consentTextHash = consentHash;
    if (this.currentBankId !== undefined) payload.bankId = this.currentBankId;
    if (snapshot.monthlyPayment !== undefined) payload.monthlyPayment = snapshot.monthlyPayment;
    // Состав семьи — только по «Семейной ипотеке»: снимок отдаёт эти поля лишь
    // когда выбрана программа config.family.programId, по остальным их в заявке нет.
    if (snapshot.familyChildren !== undefined) payload.familyChildren = snapshot.familyChildren;
    if (snapshot.familyDisabledChild !== undefined) payload.familyDisabledChild = snapshot.familyDisabledChild;

    // ── Блокируем кнопку на время запроса (защита от дабл-сабмита) ──
    const submitBtn = this.deps.root.querySelector<HTMLButtonElement>('[data-action="submit-lead"]');
    if (submitBtn) submitBtn.disabled = true;

    // ── Свежий embed-токен: без него сервер отбивает лид 403 ──
    const token = await this.deps.getEmbedToken();
    if (!token) {
      if (submitBtn) submitBtn.disabled = false;
      this.showError('Не удалось связаться с сервером. Проверьте интернет и попробуйте ещё раз.');
      return;
    }

    // ── POST. Не подтвердили приём — честно показываем ошибку, НЕ фабрикуем успех.
    // Раньше catch тихо звал onSuccess/showSuccess и лид терялся молча (feedback 2026-07-01).
    try {
      const res = await fetch(this.deps.submitEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Embed-Token': token,
        },
        credentials: 'omit',
        ...within(20_000),
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`submit_${res.status}`);
      const json = (await res.json().catch(() => null)) as { ok?: boolean } | null;
      if (json && json.ok === false) throw new Error('submit_rejected');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[wl-mortgage] lead submit failed', err);
      if (submitBtn) submitBtn.disabled = false;
      // Тот же текст для сетевой ошибки и для таймаута — AbortSignal.timeout
      // бросает тот же тип исключения, что и обрыв сети, и человеку в обоих
      // случаях нужно одно и то же: «не достучались, попробуйте ещё раз».
      this.showError('Не удалось отправить заявку. Попробуйте ещё раз или позвоните нам.');
      return;
    }

    if (submitBtn) submitBtn.disabled = false;
    this.deps.onSuccess?.(payload);
    this.showSuccess();
  }

  /**
   * Показать пользователю ошибку прямо в форме — строкой с role="alert", а не
   * системным диалогом: тот блокирует вкладку, а на мобильных модалка под ним
   * пропадает из виду. Форма и введённые значения остаются на месте —
   * повторная отправка идёт без перезаполнения.
   */
  private showError(message: string): void {
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  private hideError(): void {
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  /** Красная обводка у полей, не прошедших валидацию; у остальных — снята. */
  private markInvalid(fields: HTMLElement[]): void {
    const invalidSet = new Set(fields);
    [this.inpName, this.inpPhone, this.inpConsent].forEach((f) => {
      if (invalidSet.has(f)) f.setAttribute('aria-invalid', 'true');
      else f.removeAttribute('aria-invalid');
    });
  }

  private clearInvalid(): void {
    [this.inpName, this.inpPhone, this.inpConsent].forEach((f) => f.removeAttribute('aria-invalid'));
  }

  private showSuccess(): void {
    this.formEl.classList.add('hidden');
    this.successEl.classList.add('show');
    this.autoCloseTimer = setTimeout(() => this.close(), 3000);
  }

  destroy(): void {
    document.removeEventListener('keydown', this.keydownHandler);
    if (this.autoCloseTimer) clearTimeout(this.autoCloseTimer);
  }
}

/**
 * Маска +7 (XXX) XXX-XX-XX.
 * Идёт по цифрам из ввода, обрезает до 11.
 */
function mustQuery<T extends Element>(root: ShadowRoot, selector: string): T {
  const el = root.querySelector<T>(selector);
  if (!el) throw new Error(`[wl-mortgage] missing element: ${selector}`);
  return el;
}
